import { CommonModule } from '@angular/common';
import { Component, ElementRef, Signal, ViewChild, computed } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { IncidentDetailsPageStore } from '../../incident-details-page.store';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';
import { firstValueFrom } from 'rxjs';
import { MediaReferenceMetadataCreateModel } from '../../models/media-reference-metadata-create.model';
import { ToastrService } from 'ngx-toastr';
import { MediaReferenceHelperService } from './media-reference-helper.service';
import { FieldReferenceService } from '../../field-references.service';

@Component({
  standalone: true,
  selector: 'app-media-references',
  imports: [CommonModule, MatIconModule],
  providers: [MediaReferenceHelperService],
  templateUrl: './media-references.component.html',
  styleUrl: './media-references.component.scss',
})
export class MediaReferencesComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly maxAllowedFizeSizeBytes = 5 * 1024 * 1024; // 5MB

  protected readonly metadataReferences: Signal<MediaReferenceMetadataModel[]> = computed(() => {
    return this.store
      .allMediaReferences()
      .filter((reference): reference is MediaReferenceMetadataModel => this.isMediaReferenceMetadata(reference));
  });

  protected readonly createModelReferences: Signal<MediaReferenceMetadataCreateModel[]> = computed(() => {
    return (
      this.store.allMediaReferences() as (MediaReferenceMetadataModel | MediaReferenceMetadataCreateModel)[]
    ).filter((reference): reference is MediaReferenceMetadataCreateModel =>
      this.isMediaReferenceCreateModel(reference),
    );
  });

  constructor(
    protected readonly mediaReferenceHelper: MediaReferenceHelperService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    protected readonly store: IncidentDetailsPageStore,
    protected readonly fieldReferenceService: FieldReferenceService,
  ) {}

  protected deleteReference = async (
    mediaReferenceToDelete: MediaReferenceMetadataModel | MediaReferenceMetadataCreateModel,
  ): Promise<void> => {
    const dialogRef = this.dialog.open<
      ConfirmationModalComponent,
      { title: string; content: string; confirmButtonText: string },
      boolean
    >(ConfirmationModalComponent, {
      data: {
        title: 'Delete reference?',
        content: `This reference will be permanently removed from the incident. This action can't be undone.`,
        confirmButtonText: 'Delete media reference',
      },
    });

    const dialogResult = await firstValueFrom(dialogRef.afterClosed());
    if (dialogResult == false) {
      return;
    }

    if (this.isCreateModel(mediaReferenceToDelete)) {
      // Check if it's been added in edit mode. If so, remove it from there.
      // We check the URL since there is no id for these references at this point.
      const createModel = mediaReferenceToDelete as MediaReferenceMetadataCreateModel;

      this.store.mediaReferencesToAdd.update((referencesToAdd) =>
        referencesToAdd!.filter((reference) => reference.createId !== createModel.createId),
      );
    } else {
      // If not, it's an already stored reference and we need to mark it for deletion and remove it from the snapshot collection.
      this.store.mediaReferencesSnapshot.update((references) =>
        references!.filter((reference) => reference.id !== mediaReferenceToDelete.id),
      );
      this.store.mediaReferenceIdsToDelete.update((referencesForDeletion) => [
        ...(referencesForDeletion ?? []),
        mediaReferenceToDelete.id,
      ]);
    }
  };

  protected deleteReferenceFromMerge = (mediaReferenceToDelete: MediaReferenceMetadataModel): void => {
    this.fieldReferenceService.removeMediaFieldReference(mediaReferenceToDelete);
  };

  protected uploadMedia(): void {
    // Clicks an invisible input field which opens the Windows file explorer and then triggers onFileSelected().
    this.fileInput.nativeElement.click();
  }

  protected isMediaReferenceMetadata(value: unknown): value is MediaReferenceMetadataModel {
    if (typeof value === 'object' && value !== null && 'id' in value && 'contentType' in value) {
      return true;
    }

    return false;
  }

  protected isMediaReferenceCreateModel(value: unknown): value is MediaReferenceMetadataCreateModel {
    if (typeof value === 'object' && value !== null && 'createId' in value && 'file' in value) {
      return true;
    }

    return false;
  }

  protected createModelDisplayName(index: number): string {
    return `File ${index + 1} (new)`;
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
    ];

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Unsupported media type selected.', 'Upload media reference', {
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    // Validate file size
    if (file.size > this.maxAllowedFizeSizeBytes) {
      this.toastr.error('File exceeds the 5MB limit.', 'Upload media reference', {
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    // File is valid, store it.
    const createModel: Partial<MediaReferenceMetadataCreateModel> = {
      createId: crypto.randomUUID(), // create an Id only used in the edit mode to determine which item to delete, if requested.
      contentType: file.type,
      file: file,
    };

    this.store.mediaReferencesToAdd.update((referencesToAdd) => [
      ...(referencesToAdd ?? []),
      createModel as MediaReferenceMetadataCreateModel,
    ]);
  }

  private isCreateModel(
    model: MediaReferenceMetadataModel | MediaReferenceMetadataCreateModel,
  ): model is MediaReferenceMetadataCreateModel {
    return typeof (model as MediaReferenceMetadataCreateModel).createId === 'string';
  }
}
